import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, ParamMap, Router } from '@angular/router';
import {
  ReportId,
  ReportInputParameterKeys,
  ReportingComparisonScopes,
  isKnownReportId,
  type AgencyBrandingOptionValue,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { combineLatest } from 'rxjs';
import { toAgencyBrandingOptionValue } from '../../utils/agency-branding-options.utils';
import { parseReportScopeParam } from '../../utils/report-sections.utils';

interface RouteState {
  reportId: string;
  generatedAt: string | null;
  /** Where "back to reports list" should go; see goBackToReportsList. */
  returnUrl: string | null;
  brandingId: AgencyBrandingOptionValue | null;
  variables: Record<string, unknown>;
  /** The report section this page was opened from; sent with every request. */
  scope: ReportingComparisonScope;
  saveDefaultsOnApply: boolean;
  /** Whether the report starts from the saved default settings (ARM) or the built-in ones (portal). */
  useStoredDefaults: boolean;
  requestKey: string;
}

@Component({
  selector: 'ag-shr-production-report-viewer-page',
  templateUrl: './production-report-viewer-page.component.html',
  styleUrls: ['./production-report-viewer-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'production-report-viewer production-report-viewer-page' },
  encapsulation: ViewEncapsulation.None,
})
export class ProductionReportViewerPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly routeStateSig = signal<RouteState | null>(null);
  protected readonly routeErrorSig = signal<string | null>(null);

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap, this.route.data])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([paramMap, queryParamMap, routeData]) => {
        this.syncRouteState(paramMap, queryParamMap, routeData);
      });
  }

  protected goBack = (): void => {
    this.location.back();
  };

  /**
   * `['../']` only works where the report id is its own nested route — it strips one
   * segment, and a route declared as `production-report-viewer/:reportId` is a
   * single two-segment route, so relative navigation lands on a path that matches
   * nothing. A caller that reached this page from elsewhere passes `return-url`.
   */
  protected goBackToReportsList(): void {
    const returnUrl = this.routeStateSig()?.returnUrl;

    if (returnUrl) {
      void this.router.navigateByUrl(returnUrl);

      return;
    }

    void this.router.navigate(['../'], { relativeTo: this.route });
  }

  /**
   * Same-origin relative paths only. The value comes from the query string, so an
   * absolute URL here would be an open redirect.
   */
  private parseReturnUrl(queryParamMap: ParamMap): string | null {
    const rawReturnUrl = queryParamMap.get('return-url');

    if (!rawReturnUrl || !rawReturnUrl.startsWith('/') || rawReturnUrl.startsWith('//')) {
      return null;
    }

    return rawReturnUrl;
  }

  private buildRouteState(paramMap: ParamMap, queryParamMap: ParamMap, routeData: Data): RouteState {
    const variables = this.parseVariables(queryParamMap);
    const brandingId = this.parseBrandingId(queryParamMap);
    const reportId = this.normalizeReportId(paramMap.get('reportId') ?? '');
    // Links from before scopes existed carry none; they came from the company-wide viewer.
    const scope = parseReportScopeParam(queryParamMap.get('scope')) ?? ReportingComparisonScopes.allianceGroup;
    const saveDefaultsOnApply = Boolean(routeData['saveDefaultsOnApply']);
    const useStoredDefaults = Boolean(routeData['useStoredDefaults']);
    const requestKey = this.buildRouteRequestKey(reportId, queryParamMap);

    return {
      reportId,
      generatedAt: queryParamMap.get('generatedAt'),
      returnUrl: this.parseReturnUrl(queryParamMap),
      brandingId,
      variables,
      scope,
      saveDefaultsOnApply,
      useStoredDefaults,
      requestKey,
    };
  }

  private syncRouteState(paramMap: ParamMap, queryParamMap: ParamMap, routeData: Data): void {
    const routeState = this.buildRouteState(paramMap, queryParamMap, routeData);
    if (this.routeStateSig()?.requestKey === routeState.requestKey) {
      return;
    }

    this.routeStateSig.set(routeState);
  }

  protected onAppliedVariablesChange(variables: Record<string, unknown>): void {
    const queryParams = this.buildAppliedVariableQueryParams(variables);
    if (!Object.keys(queryParams).length) {
      return;
    }

    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  private buildAppliedVariableQueryParams(variables: Record<string, unknown>): Record<string, string | number | null> {
    const queryParams: Record<string, string | number | null> = {
      period: this.normalizeQueryParamValue(variables['period']),
      'period-ending': this.normalizeQueryParamValue(variables['period-ending']),
      // Resolved scalar parameters take precedence; remove legacy JSON dates on refresh.
      variables: null,
    };
    const startDate = this.normalizeQueryParamValue(variables[ReportInputParameterKeys.startDate]);
    const endDate = this.normalizeQueryParamValue(variables[ReportInputParameterKeys.endDate]);

    if (startDate !== null) {
      queryParams['start-date'] = startDate;
      queryParams[ReportInputParameterKeys.startDate] = null;
      queryParams['startDate'] = null;
    }

    if (endDate !== null) {
      queryParams['end-date'] = endDate;
      queryParams[ReportInputParameterKeys.endDate] = null;
      queryParams['endDate'] = null;
    }

    const displayThreshold = this.normalizeQueryParamValue(variables[ReportInputParameterKeys.displayThreshold]);
    queryParams[ReportInputParameterKeys.displayThreshold] = displayThreshold;
    // Only the off state is written, so a link with the threshold on stays as it was.
    const includeThreshold = variables[ReportInputParameterKeys.includeThreshold];
    queryParams[ReportInputParameterKeys.includeThreshold] =
      includeThreshold === false || includeThreshold === 'false' ? 'false' : null;

    for (const key of Object.values(ReportInputParameterKeys)) {
      if (key === ReportInputParameterKeys.startDate || key === ReportInputParameterKeys.endDate) continue;
      const value = variables[key];
      if (value !== undefined && value !== null)
        queryParams[key] = typeof value === 'boolean' ? String(value) : this.normalizeQueryParamValue(value);
    }
    return queryParams;
  }

  private normalizeQueryParamValue(value: unknown): string | number | null {
    if (value === undefined || value === null) {
      return null;
    }

    const normalizedValue = typeof value === 'number' ? value : String(value).trim();
    return normalizedValue === '' ? null : normalizedValue;
  }

  private buildRouteRequestKey(reportId: string, queryParamMap: ParamMap): string {
    const routePart = reportId;
    const queryPart = queryParamMap.keys
      .slice()
      .sort()
      .map(key => `${key}:${queryParamMap.getAll(key).join(',')}`)
      .join('|');

    return `${routePart}|${queryPart}`;
  }

  private parseVariables(queryParamMap: ParamMap): Record<string, unknown> {
    const rawVariables = queryParamMap.get('variables');
    const parsedValue = this.parseJson(rawVariables);

    const parsedVariables =
      parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
        ? { ...(parsedValue as Record<string, unknown>) }
        : {};

    Object.values(ReportInputParameterKeys).forEach(key => {
      const queryValue = this.getVariableQueryParamValue(queryParamMap, key);
      if (queryValue !== null) parsedVariables[key] = queryValue;
    });

    parsedVariables['period'] = queryParamMap.get('period');
    parsedVariables['period-ending'] = queryParamMap.get('period-ending');
    return parsedVariables;
  }

  private getVariableQueryParamValue(queryParamMap: ParamMap, key: ReportInputParameterKeys): string | null {
    const aliases = this.getVariableQueryParamAliases(key);

    for (const alias of aliases) {
      const value = queryParamMap.get(alias);
      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  private getVariableQueryParamAliases(key: ReportInputParameterKeys): string[] {
    switch (key) {
      case ReportInputParameterKeys.startDate:
        return ['start-date', 'startDate', ReportInputParameterKeys.startDate];
      case ReportInputParameterKeys.endDate:
        return ['end-date', 'endDate', ReportInputParameterKeys.endDate];
      default:
        return [key];
    }
  }

  private parseBrandingId(queryParamMap: ParamMap): AgencyBrandingOptionValue | null {
    const rawValue = queryParamMap.get('brandingId') ?? queryParamMap.get('branding_id');
    return toAgencyBrandingOptionValue(rawValue);
  }

  private parseJson(rawValue: string | null): unknown {
    if (!rawValue) return null;

    const parsers: Array<() => unknown> = [() => JSON.parse(rawValue), () => JSON.parse(decodeURIComponent(rawValue))];

    for (const parser of parsers) {
      try {
        return parser();
      } catch {
        // try next
      }
    }

    return rawValue;
  }

  private normalizeReportId(rawReportId: string): ReportId | string {
    if (isKnownReportId(rawReportId)) {
      return rawReportId;
    }

    const underscoredReportId = rawReportId.replace(/-/g, '_');
    return isKnownReportId(underscoredReportId) ? underscoredReportId : rawReportId;
  }
}
